/* eslint-disable max-lines */
import { useCheckFeePromotions } from '@melio/ap-widgets';
import { withAnalyticsContext } from '@melio/platform-analytics';
import {
  DeliveryMethodByPayor,
  SupportedDeliveryMethodTypeOption,
  useDeliveryMethods,
  useFundingSource,
  useVendor,
} from '@melio/platform-api';
import { useConfig } from '@melio/platform-provider';
import { useEffect, useMemo } from 'react';

import { AddInternationalDeliveryMethodActivity } from '../../international-payment';
import { AddVendorBankDetailsActivity } from '../AddVendorBankDetails';
import { AddVendorCheckDetailsActivity } from '../AddVendorCheckDetails';
import { AddVendorDomesticWireDetailsActivity } from '../AddVendorDomesticWireDetails';
import { AddVendorUnilateralDetailsActivity } from '../AddVendorUnilateralDetails';
import { AddVendorVirtualCardDetailsActivity } from '../AddVendorVirtualCardDetails';
import { EditVendorBankDetailsActivity } from '../EditVendorBankDetails';
import { EditVendorCheckDetailsActivity } from '../EditVendorCheckDetails';
import { EditVendorDomesticWireDetailsActivity } from '../EditVendorDomesticWireDetails';
import { RequestVendorUnilateralDetailsActivity } from '../RequestVendorUnilateralDetails';
import { filterAndGroupDeliveryMethodTypeOptionsByPartner } from './filterAndSplitDeliveryMethodTypeOptionsIntoGroups';
import { DeliveryMethodSelectionScreen } from './screens';
import { DeliveryMethodSelectionActivityProps } from './types';
import { useDeliveryMethodSelectionActivityStep } from './useDeliveryMethodSelectionActivityStep';

export const DeliveryMethodSelectionActivity = withAnalyticsContext<DeliveryMethodSelectionActivityProps>(
  ({
    vendorId,
    selectedId,
    fundingSourceId,
    step,
    totalSteps,
    onSkip,
    onBack: onFirstStepBack,
    onClose,
    onError,
    onDone,
    origin = 'payment',
    isLoading,
    deliveryMethodTypeOptions = [],
    setAnalyticsProperties,
  }) => {
    const deliveryMethodCollection = useDeliveryMethods({ vendorId });
    const { freeChecks, isLoading: isFreeChecksLoading } = useCheckFeePromotions();

    const vendorModel = useVendor({ id: vendorId });
    const vendorName = vendorModel.data?.name || '';
    const fundingSourceModel = useFundingSource({ id: fundingSourceId });
    const { settings } = useConfig();

    const { currentStep, goToStep, goToPreviousStep } = useDeliveryMethodSelectionActivityStep({ onFirstStepBack });

    function findDeliveryMethodByType(type: DeliveryMethodByPayor['type']) {
      return deliveryMethodCollection.data?.find((deliveryMethod) => deliveryMethod.type === type);
    }

    const { domesticDeliveryMethodTypeOptions, internationalDeliveryMethodTypeOptions } = useMemo(
      () =>
        filterAndGroupDeliveryMethodTypeOptionsByPartner(
          deliveryMethodTypeOptions,
          settings.deliveryMethodTypeOptionDisabledReasons
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [deliveryMethodTypeOptions]
    );

    const concatDmTypesAlphabetical = (deliveryMethods: SupportedDeliveryMethodTypeOption[]) =>
      deliveryMethods
        .map((d) => d.type)
        .sort()
        .join('_');

    useEffect(() => {
      setAnalyticsProperties({
        DeliveryMethodShown: concatDmTypesAlphabetical([
          ...domesticDeliveryMethodTypeOptions,
          ...internationalDeliveryMethodTypeOptions,
        ]),
        DeliveryMethodDisabled: concatDmTypesAlphabetical(
          [...domesticDeliveryMethodTypeOptions, ...internationalDeliveryMethodTypeOptions].filter(
            (dm) => !dm.supported
          )
        ),
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domesticDeliveryMethodTypeOptions, internationalDeliveryMethodTypeOptions]);

    const isDeliveryMethodSelectionLoading =
      deliveryMethodCollection.isLoading ||
      vendorModel.isLoading ||
      fundingSourceModel.isLoading ||
      isFreeChecksLoading ||
      isLoading;

    switch (currentStep) {
      case 'DELIVERY_METHOD_SELECTION':
      default:
        return (
          <DeliveryMethodSelectionScreen
            isLoading={isDeliveryMethodSelectionLoading}
            fundingSource={fundingSourceModel.data}
            freeChecks={freeChecks}
            step={step}
            totalSteps={totalSteps}
            data={(deliveryMethodCollection.data as DeliveryMethodByPayor[]) || []}
            selectedId={selectedId}
            domesticDeliveryMethodTypeOptions={domesticDeliveryMethodTypeOptions}
            internationalDeliveryMethodTypeOptions={internationalDeliveryMethodTypeOptions}
            vendorName={vendorName}
            onBack={goToPreviousStep}
            onClose={onClose}
            onEdit={(type) => {
              switch (type) {
                case 'bank-account':
                  goToStep('EDIT_VENDOR_BANK_DETAILS');
                  break;
                case 'paper-check':
                  goToStep('EDIT_VENDOR_CHECK_DETAILS');
                  break;
                case 'domestic-wire-account':
                  goToStep('EDIT_VENDOR_DOMESTIC_WIRE_DETAILS');
                  break;
                case 'virtual-card':
                  goToStep('EDIT_VENDOR_VIRTUAL_CARD_DETAILS');
                  break;
              }
            }}
            onAdd={(type) => {
              switch (type) {
                case 'bank-account':
                  goToStep('ADD_VENDOR_BANK_DETAILS');
                  break;
                case 'paper-check':
                  goToStep('ADD_VENDOR_CHECK_DETAILS');
                  break;
                case 'domestic-wire-account':
                  goToStep('ADD_VENDOR_DOMESTIC_WIRE_DETAILS');
                  break;
                case 'virtual-account':
                  goToStep('ADD_VENDOR_UNILATERAL_DETAILS');
                  break;
                case 'international-account':
                  goToStep('ADD_VENDOR_INTERNATIONAL_DETAILS');
                  break;
                case 'virtual-card':
                  goToStep('ADD_VENDOR_VIRTUAL_CARD_DETAILS');
                  break;
              }
            }}
            onDone={(deliveryMethodId) =>
              onDone(deliveryMethodCollection.data?.find((d) => d.id === deliveryMethodId) as DeliveryMethodByPayor)
            }
            onSkip={onSkip}
          />
        );

      case 'ADD_VENDOR_BANK_DETAILS':
        return (
          <AddVendorBankDetailsActivity
            vendorId={vendorId}
            origin={origin}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
          />
        );

      case 'EDIT_VENDOR_VIRTUAL_CARD_DETAILS':
        return (
          <AddVendorVirtualCardDetailsActivity
            vendorId={vendorId}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
            origin={origin}
            isEdit
          />
        );

      case 'EDIT_VENDOR_BANK_DETAILS': {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const deliveryMethodId = findDeliveryMethodByType('bank-account')!.id;

        return (
          <EditVendorBankDetailsActivity
            vendorId={vendorId}
            deliveryMethodId={deliveryMethodId}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
          />
        );
      }
      case 'ADD_VENDOR_CHECK_DETAILS':
        return (
          <AddVendorCheckDetailsActivity
            vendorId={vendorId}
            origin={origin}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
          />
        );

      case 'EDIT_VENDOR_CHECK_DETAILS': {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const deliveryMethodId = findDeliveryMethodByType('paper-check')!.id;
        return (
          <EditVendorCheckDetailsActivity
            vendorId={vendorId}
            deliveryMethodId={deliveryMethodId}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
          />
        );
      }

      case 'ADD_VENDOR_DOMESTIC_WIRE_DETAILS':
        return (
          <AddVendorDomesticWireDetailsActivity
            vendorId={vendorId}
            origin={origin}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
          />
        );

      case 'EDIT_VENDOR_DOMESTIC_WIRE_DETAILS': {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const deliveryMethodId = findDeliveryMethodByType('domestic-wire-account')!.id;
        return (
          <EditVendorDomesticWireDetailsActivity
            vendorId={vendorId}
            deliveryMethodId={deliveryMethodId}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
          />
        );
      }

      case 'ADD_VENDOR_UNILATERAL_DETAILS':
        return origin === 'vendor' ? (
          <RequestVendorUnilateralDetailsActivity
            vendorId={vendorId}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
          />
        ) : (
          <AddVendorUnilateralDetailsActivity
            vendorId={vendorId}
            fundingSourceId={fundingSourceId}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
          />
        );
      case 'ADD_VENDOR_VIRTUAL_CARD_DETAILS':
        return (
          <AddVendorVirtualCardDetailsActivity
            vendorId={vendorId}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
            origin={origin}
          />
        );
      case 'ADD_VENDOR_INTERNATIONAL_DETAILS':
        return (
          <AddInternationalDeliveryMethodActivity
            vendorId={vendorId}
            onBack={goToPreviousStep}
            onClose={onClose}
            onDone={onDone}
            onError={onError}
            origin={origin}
          />
        );
    }
  }
);
